import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTestQuestionData,
  useTestQuestionUpdate,
} from "@repo/edtech-api";

import { buildUserAnswer } from "../../../../../lib";
import { BackTestQuestionType } from "../../../../../model";
import { ClosedOptions } from "../closed-options";
import { OpenInput } from "../open-input";
import { SurveyRating } from "../survey-rating";

import st from "./SurveyQuestionItem.module.scss";

const RATING_MAX = 10;

interface QuestionState {
  maxRating: number,
  parsedAnswers: string[],
  canUploadFile: boolean,
}

interface Props {
  questionUid: string,
  questionText: string,
  questionType: BackTestQuestionType,
  publicationId: string,
  unitId: string,
  session: string,
  testId: string,
  onAnswerChanged: (questionUid: string, isAnswered: boolean) => void,
  onRegisterSubmitHandler: (questionUid: string, submitHandler: (() => Promise<void>) | null) => void,
  onInitialized: () => void,
  // storybook prop
  initialQuestionState?: QuestionState,
  // storybook prop
  initialRating?: number | null,
  // storybook prop
  initialOpenInputValue?: string,
  // storybook prop
  initialSelectedOptionIndex?: string,
}
export const SurveyQuestionItem: FC<Props> = ({
  questionUid,
  questionText,
  questionType,
  publicationId,
  unitId,
  session,
  testId,
  onAnswerChanged,
  onRegisterSubmitHandler,
  onInitialized,
  initialQuestionState,
  initialRating = null,
  initialOpenInputValue,
  initialSelectedOptionIndex,
}) => {
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ openInputValue, setOpenInputValue ] = useState(initialOpenInputValue ?? "");

  const [ rating, setRating ] = useState<number | null>(initialRating);
  const [ selectedOptionIndex, setSelectedOptionIndex ] = useState<string | null>(initialSelectedOptionIndex ?? null);

  const [ questionState, setQuestionState ] = useState<QuestionState | null>(initialQuestionState ?? null);

  const initializedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const { data: questionData } = useTestQuestionData({
    publicationId,
    unitId,
    questionId: questionUid,
    session,
    testId,
  });

  const { trigger: updateAnswer } = useTestQuestionUpdate({
    publicationId,
    unitId,
    questionId: questionUid,
    session,
    questionType,
  });

  useEffect(() => {
    if (!questionData || initializedRef.current) return;

    initializedRef.current = true;

    const maxRatingNum = Number(questionData.stars);
    const maxRating = Number.isFinite(maxRatingNum) && maxRatingNum > 0 ?
      maxRatingNum :
      RATING_MAX;

    let parsedAnswers: string[] = [];
    if (questionType === BackTestQuestionType.Closed && questionData.answer) {
      parsedAnswers = questionData.answer.split("|").map((s) => s.trim())
        .filter(Boolean);
    } else if (questionData.answers) {
      try {
        parsedAnswers = JSON.parse(questionData.answers) as string[];
      } catch {
        parsedAnswers = [];
      }
    }

    setQuestionState({
      maxRating,
      parsedAnswers,
      canUploadFile: questionData.uploadFile === "checked",
    });

    onInitialized();
  }, [ questionData, onInitialized ]);

  const submitAnswer = useCallback(async (answerValue: string, file?: string) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    setIsSubmitting(true);
    try {
      await updateAnswer({
        answerValue,
        fileAnswer: file,
        signal: abortControllerRef.current.signal,
      });
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [ updateAnswer ]);

  const handleSurveySubmit = useCallback((value: number) => {
    setRating(value);
  }, []);

  const handleClosedSubmit = useCallback((indexStr: string) => {
    setSelectedOptionIndex(indexStr);
  }, []);

  const handleOpenInputChange = useCallback((value: string) => {
    setOpenInputValue(value);
  }, []);

  const isSurveyType = questionType === BackTestQuestionType.Survey;
  const isOpenType = questionType === BackTestQuestionType.Open;
  const isClosedType = questionType === BackTestQuestionType.Closed;

  const answerValue = useMemo(() => {
    if (isSurveyType) {
      return rating === null ?
        "" :
        buildUserAnswer(rating, "");
    }

    if (isClosedType) {
      return selectedOptionIndex ?? "";
    }

    if (isOpenType) {
      return openInputValue.trim();
    }

    return "";
  }, [
    isClosedType,
    isOpenType,
    isSurveyType,
    openInputValue,
    rating,
    selectedOptionIndex,
  ]);

  useEffect(() => {
    onAnswerChanged(questionUid, Boolean(answerValue));
  }, [
    answerValue,
    onAnswerChanged,
    questionUid,
  ]);

  const submitCurrentAnswer = useCallback(async () => {
    if (!answerValue) return;

    await submitAnswer(answerValue);
  }, [ answerValue, submitAnswer ]);

  useEffect(() => {
    onRegisterSubmitHandler(questionUid, submitCurrentAnswer);

    return () => {
      onRegisterSubmitHandler(questionUid, null);
    };
  }, [
    onRegisterSubmitHandler,
    questionUid,
    submitCurrentAnswer,
  ]);

  const showOpenInput = isOpenType;
  const showClosedOptions = isClosedType && (questionState?.parsedAnswers.length ?? 0) > 0;
  const showSurvey = isSurveyType && questionState?.maxRating;
  const showQuestion = showSurvey || showClosedOptions || showOpenInput;

  if (!showQuestion) return null;

  return (
    <div className={st.question_item}>
      <p className={st.question_text}>{questionText}</p>

      {
        isSurveyType && questionState && (
          <SurveyRating
            maxRating={questionState.maxRating}
            rating={rating}
            onSubmit={handleSurveySubmit}
          />
        )
      }

      {
        showOpenInput && questionState && (
          <OpenInput
            disabled={isSubmitting}
            value={openInputValue}
            onChange={handleOpenInputChange}
          />
        )
      }

      {
        showClosedOptions && questionState && (
          <ClosedOptions
            answers={questionState.parsedAnswers}
            questionUid={questionUid}
            selectedOptionIndex={selectedOptionIndex}
            onSubmit={handleClosedSubmit}
          />
        )
      }
    </div>
  );
};
